/**
 * Action manager
 * @module
 */

var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');
var ApiEvents = require('../api_events');
var AsyncRequest = require('../utils/async_request');
var createError = require('../utils/custom_error').create;
var DateEnv = require('../env/date');
var di = require('../di');
var Flux = require('../flux');
var Logger = require('../utils/logger');
var PromiseEnv = require('../lib/promise').Promise;
var Script = require('../utils/script');

var GlobalStore = di.get('stores/global');
var ActionDataStore = di.get('stores/action_data');
var ChangeDataStore = di.get('stores/change_data');
var SessionStore = di.get('stores/session');
var ChangeFactory = require('./change_factory');
var timingHelpers = require('./timing_helpers');

timingHelpers.initialize();

var ActionError = createError('ActionError');

/**
 * @param {ActionData} action
 */
exports.prepareAction = function(action:ActionData) {
  if (__INVARIANT__) {
    require('../utils/invariant').isType('ActionData', action);
  }

  Logger.debug('Action / Preparing:', action);

  // TODO: maybe replace ACTION_EXECUTED -> ACTION_PREPARED
  Flux.dispatch(actionTypes.ACTION_EXECUTED, {
    actionId: action.id,
    sessionId: SessionStore.getSessionId(),
    layerId: action.layerId,
    pageId: action.pageId,
    timestamp: DateEnv.now(),
    activationId: GlobalStore.getActivationId(),
  });

  // All changes need to know when action started to execute to timeout correctly
  var actionStartTime = DateEnv.now();

  _.forEach(action.changeSet, function(changeOrChangeId) {
    // If the change is already in the store (e.g. was loaded via DATA_LOADED or a previous "prepareAction" call, prefer that change)
    // In particular, this avoids overwriting async changes in the change_data store.
    var changeId = _.isObject(changeOrChangeId) ? changeOrChangeId.id : changeOrChangeId;
    var change = ChangeDataStore.getChange(changeId);
    if (!change) {
      Flux.dispatch(actionTypes.ADD_CHANGE, changeOrChangeId);
      change = ChangeDataStore.getChange(changeOrChangeId.id);
    }

    if (!change.src) {
      // Technically we don't need to add the raw change to the ChangeDataStore, but we do so for consistency
      // with async changes, which necessarily do show up in the ChangeDataStore.
      // If sync changes are ever included in the top-level `changes` property of the client config,
      // like async changes in the async payload, then they'll be automatically loaded and won't need to be loaded here.

      // Prepare the change that we just stored in the change store.
      prepareChange(change, action, actionStartTime);
    } else {
      // This is an async change, and hence incomplete, so we cannot prepare it yet.
      // Since it was put in the ChangeDataStore on load, the synchronous portion of `applyChange` has something to work with.
      // The data in the store will be overwritten when the async payload calls the `v1.load` API,
      // at which point we'll be able to prepare the change.

      // TODO: Maybe request async changes here. (CJS-1345)
    }
  });
};

/**
 * Prepare a change for execution by instantiating an instance of its plugin via the ChangeFactory.
 * Change preparation should only occur if its action is active but a changeApplier instance
 * does not already exist for it (meaning it hasnt already been prepared).
 *
 * @param {Change} change A complete change (if an async change, should include the async payload)
 * @param {ActionData} action
 * @param {number} [actionStartTime] Date when action was started
 * @private
 */
function prepareChange(change:Change, action:ActionData, actionStartTime?:number) {
  var actionState = ActionDataStore.getActionState(action.id);
  if (!actionState) {
    // A missing actionState inticates this action isn't active
    // (because it was cancelled before this change was ready to
    // be prepared) and therefore we want to abort preparing it.
    Logger.warn('Action / Attempted to prepare change for inactive action: ', action);
    return;
  }
  var existingChangeApplier = ActionDataStore.getChangeApplier(change.id, action.id);
  if (!_.isUndefined(existingChangeApplier)) {
    // A existing changeApplier indicates were already in the process
    // of applying this change, in which case, we want to abort preparing it.
    // This is unlikely but could occur if a page is activated, deactivated, then
    // reactivated again, causing 2 identical async changes to be in flight at the same time.
    // The first one to return should be prepared, but the second shouldn't.
    Logger.warn('Action / Attempted to prepare a change which is already being applied: ', change);
    return;
  }
  var applierData = {
    changeId: change.id,
    actionId: action.id,
    changeApplier: ChangeFactory.create(change, action, actionStartTime),
  };
  Flux.dispatch(actionTypes.SET_CHANGE_APPLIER, applierData);
}

/**
 * Execute an action whose changes have already been prepared and loaded into the ChangeDataStore.
 *
 * @param {ActionData} action
 * @returns {Promise}
 */
exports.executePreparedAction = function(action:ActionData): Promise<any> {
  if (__INVARIANT__) {
    require('../utils/invariant').isType('ActionData', action);
  }

  Logger.debug('Action / Executing:', action);

  var changeId2PromiseMap = {} as any;
  // We must try to apply every change, but we can do it in arbitrary order - if we try to apply a change
  // before one of its dependencies, the function will first recurse and try to apply the dependency.
  var changePromises = _.map(action.changeSet, function (changeOrChangeId) {
    var changeId = _.isObject(changeOrChangeId) ? changeOrChangeId.id : changeOrChangeId;
    return applyChange(changeId2PromiseMap, changeId, action, []);
  });

  return PromiseEnv.all(changePromises).then(
    function () {
      Logger.debug('changes for action id=' + action.id + ' applied');
    }
  );
};

/**
 * Given a changeId with data in the ChangeDataStore, schedule the change to be applied when all dependency changes
 * have finished being applied.  (Recurse to ensure that all dependency changes are applied themselves.)
 *
 * Wikipedia outlines this depth-first traversal algorithm:
 *   https://en.wikipedia.org/wiki/Topological_sorting#Depth-first_search
 *
 * @param {Object} changeId2PromiseMap Map of change ids to Promises that resolve when change execution succeeds or fails.
 * @param {string|number} changeId Change id to create and apply
 * @param {ActionData} action The action for this change
 * @param {Array.<string|number>} indirectlyDependentChangeIds List of changes that (directly or indirectly) depend on this change
 * @returns {Promise|undefined} Promise that resolves when this change is applied,
 *  once all dependencies are applied. undefined if there is a circular reference.
 * @private
 */
function applyChange(changeId2PromiseMap:any, changeId:string|number, action:ActionData, indirectlyDependentChangeIds:(string|number)[]): Promise<any>|undefined {
  if (_.includes(indirectlyDependentChangeIds, changeId)) {
    Logger.error('Change with id ' + changeId + ' has circular dependencies: ' + indirectlyDependentChangeIds.concat(changeId));
    // return undefined instead of promise to cut the loop -
    // so dependent PromiseEnv.all will be able to resolve
    return;
  }

  if (!changeId2PromiseMap[String(changeId)]) {
    var change = ChangeDataStore.getChange(changeId);
    if (!change) {
      var msg = 'Change with id ' + changeId + ' is absent';
      if (indirectlyDependentChangeIds.length) {
        msg += ' but listed as a dependency for ' + indirectlyDependentChangeIds[indirectlyDependentChangeIds.length - 1];
      }
      Logger.warn(msg);
      return;
    }

    changeId2PromiseMap[String(changeId)] = new PromiseEnv(function(resolve) {
      // If there is no deferred object yet - then we should schedule instantiation for this change
      // once all dependencies will be applied or failed
      var dependencyPromises = _.map(change.dependencies || [], function(dependencyChangeId) {
        return applyChange(changeId2PromiseMap, dependencyChangeId, action, indirectlyDependentChangeIds.concat([changeId]));
      });

      // TODO: Maybe request async changes in Action.prepareChange or Action.executePreparedChange. (CJS-1345)
      if (change.src) {
        // use SRC as async id so we will send only one request for all the changes with same SRC
        var src = 'change_' + change.src;
        // Promise that usually resolves after the async script has finished executing.
        // TODO: Rewrite to ensure this always resolves after the async script has finished executing. (CJS-1352)
        var asyncPayloadPromise = AsyncRequest.makeAsyncRequest(src, function() {
          // This executes synchronously if we haven't yet made a request for the given `src`.
          return Script.addScriptAsync(__ASYNC_BASE_URL__ + change.src, function() {
            // This executes asynchronously after the async script's `load` event.
            AsyncRequest.resolveRequest(src);
          });
        }).then(function() {
          // Refresh updated change from store
          var changeFromStore = ChangeDataStore.getChange(change.id);
          if (!changeFromStore) {
            ApiEvents.emitError(new ActionError('Failed to load async change from src: ' + change.src));
          }
          prepareChange(changeFromStore, action, DateEnv.now());
        });
        dependencyPromises.push(asyncPayloadPromise);
      }

      // Create separate promise to resolve once current change is completed - either succeeded or failed
      PromiseEnv.all(dependencyPromises).then(function() {
        var changeStart = DateEnv.now();
        // Now that this change is ready to apply, fetch the changeApplier for it from the action store.
        // If the changeApplier isn't found, it means the change was cancelled before this change's
        // dependencies were satisfied, in which case we no longer want to apply it.
        var changeApplier = ActionDataStore.getChangeApplier(changeId, action.id);
        if (!changeApplier) {
          // If we dont have a changeApplier for this change, resolve immediately.
          Logger.debug('Action / Not applying change ', changeId, ' - No changeApplier found.');
          resolve();
          return;
        }
        Logger.debug('Action / Applying change:', change);
        return changeApplier.apply()
          .then(function(message) {
            if (message) {
              Logger.log(message);
            } else {
              Logger.debug('Action / Applied change for the first time in ' + (DateEnv.now() - changeStart) + 'ms:', change);
            }
            (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('flash');
            resolve();
          });
      })
      ['catch'](function(err) {
        // If change failed to apply - we still need to resolve a dependency so
        // dependent changes will have a chance to apply as well
        Logger.error('Action / Failed to apply change:', change, err);
        resolve();
      });
    });
  }
  return changeId2PromiseMap[String(changeId)];
}

// Export methods for testing.
if (__TEST__) {
  exports.prepareChange = prepareChange;
}
